import { NEXT_PUBLIC_APPLE_APP_ID, NEXT_PUBLIC_API_URL } from '@env';
import { useUser } from '@context/user-context';
import { logger } from '@modules/logger';

import { useCreateOneTimeLoginTokenMutation } from './createOneTimeLoginToken.graphql';
import { DeeplinkBuilder } from './deeplink-builder';
import { useFeatureFlag } from '@hooks/use-feature-flag';
import { useEffect, useMemo, useRef } from 'react';

const appStoreDefaultDeeplink = 'https://blinqapp.page.link/zDJT';

interface IOpenAppUrlOptions {
  includeOpaqueToken?: boolean;
  /**
   * Use case for opening the app
   * monorepo/api/src/route/redirect/use-cases.ts
   */
  useCase?: 'claim' | 'signup';
}

/** Created an "Open App" deeplink */
export function useOpaqueToken({ includeOpaqueToken }: IOpenAppUrlOptions): string {
  const [createOneTimeLoginTokenMutation, result] = useCreateOneTimeLoginTokenMutation();
  const { loading } = useUser();
  const enabled = useFeatureFlag('session_continuity_qr_code') && includeOpaqueToken;

  useEffect(() => {
    if (!loading && enabled) {
      try {
        createOneTimeLoginTokenMutation();
      } catch (error) {
        logger.error('Error creating one time login token', { error });
      }
    }
  }, [loading, enabled, createOneTimeLoginTokenMutation]);

  return useMemo(() => result.data?.createOneTimeLoginToken ?? '', [result]); // memoise the result
}

/** Get a link to the play or app store */
export function useOpenAppUrl(options: IOpenAppUrlOptions): {
  appStore: string;
  playStore: string;
} {
  const apiRedirect = useFeatureFlag('get_blinq_api_redirect');
  const opaqueToken = useOpaqueToken(options);
  const v1 = useOpenAppUrlV1({ ...options, opaqueToken });
  const v2 = useOpenAppUrlV2({ ...options, opaqueToken });

  if (apiRedirect) {
    return v2;
  }
  return v1;
}

function useOpenAppUrlV2({ useCase, opaqueToken }: IOpenAppUrlOptions & { opaqueToken: string }): {
  appStore: string;
  playStore: string;
} {
  const appStoreUrl = new URL('/redirect/blinq-app', NEXT_PUBLIC_API_URL);
  const playStoreUrl = new URL('/redirect/blinq-app', NEXT_PUBLIC_API_URL);
  // set the platform value
  appStoreUrl.searchParams.set('p', 'ios');
  playStoreUrl.searchParams.set('p', 'android');

  if (useCase) {
    appStoreUrl.searchParams.set('use', useCase);
    playStoreUrl.searchParams.set('use', useCase);
  }

  if (opaqueToken) {
    appStoreUrl.searchParams.set('opaque_token', opaqueToken);
    playStoreUrl.searchParams.set('opaque_token', opaqueToken);
  }
  return { appStore: appStoreUrl.toString(), playStore: playStoreUrl.toString() };
}

/** Created an "Open App" deeplink */
function useOpenAppUrlV1({ includeOpaqueToken, opaqueToken }: IOpenAppUrlOptions & { opaqueToken: string }): {
  appStore: string;
  playStore: string;
} {
  const enabled = useFeatureFlag('session_continuity_qr_code') && includeOpaqueToken;
  const appStoreDeeplinkBuilder = useRef(new DeeplinkBuilder('https://apps.apple.com/app/id1324102258'));
  const playStoreDeeplinkBuilder = useRef(
    new DeeplinkBuilder('https://play.google.com/store/apps/details?id=com.rabbl.blinq')
  );
  if (opaqueToken) {
    appStoreDeeplinkBuilder.current.setLinkQueryParam('opaque_token', opaqueToken);
    playStoreDeeplinkBuilder.current.setLinkQueryParam('opaque_token', opaqueToken);
  }
  // include both play and apps store refs, incase the user clicks the wrong button
  appStoreDeeplinkBuilder.current
    .setDeepQueryParam('apn', 'com.rabbl.blinq')
    .setDeepQueryParam('isi', '1324102258')
    .setDeepQueryParam('ibi', `com.rabbl.${NEXT_PUBLIC_APPLE_APP_ID}`)
    .setDeepQueryParam('imv', '1.2.31')
    .setDeepQueryParam('amv', '1.1.19')
    .setDeepQueryParam('pt', '118649518') // provider token
    .setDeepQueryParam('mt', '8') // media token
    .setDeepQueryParam('ct', 'dashboard'); // campaign token

  appStoreDeeplinkBuilder.current.setLinkQueryParam('_dl', 'login');
  playStoreDeeplinkBuilder.current.setLinkQueryParam('_dl', 'login');

  playStoreDeeplinkBuilder.current
    .setDeepQueryParam('apn', 'com.rabbl.blinq')
    .setDeepQueryParam('isi', '1324102258')
    .setDeepQueryParam('ibi', `com.rabbl.${NEXT_PUBLIC_APPLE_APP_ID}`)
    .setDeepQueryParam('imv', '1.2.31')
    .setDeepQueryParam('amv', '1.1.19');

  const result = useMemo(() => {
    if (opaqueToken) {
      playStoreDeeplinkBuilder.current.setLinkQueryParam('opaque_token', opaqueToken);
      appStoreDeeplinkBuilder.current.setLinkQueryParam('opaque_token', opaqueToken);
    }
    return {
      playStore: playStoreDeeplinkBuilder.current.build(),
      appStore: enabled ? appStoreDeeplinkBuilder.current.build() : appStoreDefaultDeeplink,
    };
  }, [opaqueToken, enabled]);

  return result;
}
